// src/settings.ts
//
// User & org settings persistence.
//
// Two tables in Supabase:
//   - org_settings  (per-org: logo, company info, templates, layout order)
//   - user_settings (per-user: name, phone, email)
//
// Strategy: network first, localStorage as cache. On first load after deploy,
// if Supabase rows are empty and localStorage has data, push localStorage up
// (auto-migration). Mirrors the pattern in proposalSections.ts.

import { supabase } from "./supabaseClient";
import { durableGet, durableSet } from "./durableStore";
import { dlog } from "./debugLog";
import {
  isProposalTextEmpty,
  normalizeProposalText,
  type ProposalText,
} from "./shared/proposalText";

// UI-facing shape — what App.tsx and SettingsPage already work with.
export type AppUserSettings = {
  // Per-user (user_settings)
  userName: string;
  userPhone: string;
  userEmail: string;
  signatureUrl: string;

  // Per-org (org_settings)
  logoDataUrl: string;
  logoSlogan: string;
  companyName: string;
  companyPhone: string;
  companyAddress: string;
  companyWebsite: string;
  license: string;
  emailSubjectTemplate: string;
  emailBodyTemplate: string;
  proposalLayoutOrder: string[];
  /** Proposal-page wording overrides (labels, headings, Next steps, validity
   *  days) — see shared/proposalText.ts. Empty object = built-in text. */
  proposalText: ProposalText;

  // Anything else SettingsPage stuffs in (e.g. proposalSections during edit)
  // We pass it through but don't persist it across the org/user split.
  [key: string]: any;
};

const CACHE_KEY = "du_user_settings"; // existing key, kept as the cache

// Defaults are intentionally neutral so a brand-new user/org starts
// with blank personal + company fields and fills them in from Settings.
// Anything tenant-specific (a person's name, a company name, a
// branded email subject) would otherwise bleed into proposals sent by
// new contractors who haven't customized their settings yet.
export const DEFAULT_SETTINGS: AppUserSettings = {
  userName: "",
  userPhone: "",
  userEmail: "",
  signatureUrl: "",

  logoDataUrl: "",
  logoSlogan: "",
  companyName: "",
  companyPhone: "",
  companyAddress: "",
  companyWebsite: "",
  license: "",
  emailSubjectTemplate: "Your Proposal – {{clientLastName}}",
  emailBodyTemplate:
    "Hi {{clientFirstName}},\n\n" +
    "Thank you for the opportunity to quote your project.\n" +
    "You can view your proposal using the link below.\n\n" +
    "If you have any questions, reply here or call/text me at {{userPhone}}.\n\n" +
    "Thanks,\n" +
    "{{userName}}\n" +
    "{{companyName}}",
  proposalLayoutOrder: [],
  proposalText: {},
};

// ----------------------------------------------------------------------------
// Cache helpers
// ----------------------------------------------------------------------------
// The cache is read through src/durableStore.ts rather than raw localStorage:
// on the packaged desktop app the UI loads from file://, where localStorage is
// unreliable, so the July last-known-good fallback below had nothing to fall
// back to after an app restart. proposalLayoutOrder (the proposal block order)
// lives in this snapshot, which is why an offline desktop launch lost it.
export function readCachedSettings(): AppUserSettings | null {
  try {
    const raw = durableGet(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_SETTINGS, ...parsed };
  } catch {
    return null;
  }
}

export function writeCachedSettings(settings: AppUserSettings) {
  try {
    durableSet(CACHE_KEY, JSON.stringify(settings));
  } catch {}
}

// ----------------------------------------------------------------------------
// Detect whether a cache snapshot has any user-entered data worth migrating.
// (Distinguishes a real saved snapshot from the empty defaults.)
// ----------------------------------------------------------------------------
function hasMeaningfulData(s: AppUserSettings | null): boolean {
  if (!s) return false;
  const fields = [
    s.userPhone,
    s.userEmail,
    s.logoDataUrl,
    s.companyPhone,
    s.companyAddress,
    s.companyWebsite,
    s.license,
    s.logoSlogan,
  ];
  if (fields.some((v) => typeof v === "string" && v.trim().length > 0)) return true;
  if (Array.isArray(s.proposalLayoutOrder) && s.proposalLayoutOrder.length > 0)
    return true;
  if (!isProposalTextEmpty(s.proposalText)) return true;
  // userName/companyName/templates have defaults — only count if user changed them
  if (s.userName && s.userName !== DEFAULT_SETTINGS.userName) return true;
  if (s.companyName && s.companyName !== DEFAULT_SETTINGS.companyName) return true;
  if (
    s.emailSubjectTemplate &&
    s.emailSubjectTemplate !== DEFAULT_SETTINGS.emailSubjectTemplate
  )
    return true;
  if (
    s.emailBodyTemplate &&
    s.emailBodyTemplate !== DEFAULT_SETTINGS.emailBodyTemplate
  )
    return true;
  return false;
}

// ----------------------------------------------------------------------------
// Fetch: Supabase first, merge org + user; auto-migrate cache when both empty.
//
// The org_settings.logo_data_url column currently holds a base64-inlined PNG
// (~1.3 MB for the Decks Unique logo). On a cellular connection that single
// cell can stall or abort the response — and because everything used to be
// one Promise.all, a failure there rejected the whole fetchSettings and
// blanked phone/email/company-text on mobile.
//
// To decouple: split the org_settings read into a "text" query (the small
// fields callers always need) and a separate "logo" query (the big blob).
// All three legs run in parallel via Promise.allSettled, the logo leg gets
// its own short timeout, and a logo failure no longer drops the text values.
// A cached logoDataUrl is preserved when the cloud fetch fails so we don't
// regress the already-cached desktop state on a flaky boot. fetchOrgLogo is
// exported so callers (mobile) can retry the logo lazily after the main
// settings have already arrived.
//
// Step 3 (move the logo to Supabase Storage like the signature does) is the
// real long-term fix — this is the short-term unblock so mobile renders the
// text fields immediately even while the 1.3 MB blob is in flight.
// ----------------------------------------------------------------------------

const ORG_TEXT_COLUMNS =
  "logo_slogan, company_name, company_phone, company_address, company_website, license, email_subject_template, email_body_template, proposal_layout_order, proposal_text";
const ORG_LOGO_COLUMN = "logo_data_url";
const USER_COLUMNS = "user_name, user_phone, user_email, signature_url";

// Cap how long we'll wait for the big logo blob before we stop blocking the
// rest of the settings fetch. Tuned generously: a 1.3 MB payload on a
// healthy 4G uplink completes in ~2s; we give it 8s before considering it
// timed out. The underlying supabase-js request keeps running in the
// background — we just stop awaiting it.
const LOGO_FETCH_TIMEOUT_MS = 8000;

function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(`${label} timeout after ${ms}ms`)),
      ms
    );
    p.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e) => {
        clearTimeout(timer);
        reject(e);
      }
    );
  });
}

/**
 * Fetch just the org's logo data URL. Used both inside fetchSettings (as one
 * of the three parallel legs) and as a standalone lazy-retry path for mobile
 * — when the first attempt timed out on cell, the caller can try again
 * after the main settings render so the logo eventually appears without
 * blocking phone/email/company text.
 *
 * Resolves to the data URL string when present, `null` on failure or when
 * the row has no logo. Never throws — failures surface as `null`.
 */
export async function fetchOrgLogo(orgId: string): Promise<string | null> {
  if (!orgId) return null;
  const t0 =
    typeof performance !== "undefined" ? performance.now() : Date.now();
  try {
    const res = await supabase
      .from("org_settings")
      .select(ORG_LOGO_COLUMN)
      .eq("org_id", orgId)
      .maybeSingle();
    const ms = Math.round(
      (typeof performance !== "undefined" ? performance.now() : Date.now()) - t0
    );
    if (res.error) {
      // eslint-disable-next-line no-console
      console.warn("[fetchOrgLogo] error in", ms, "ms:", res.error);
      return null;
    }
    const logo = ((res.data as any)?.logo_data_url || "") as string;
    dlog("[fetchOrgLogo] ok in", ms, "ms, len:", logo.length);
    return logo || null;
  } catch (err: any) {
    // eslint-disable-next-line no-console
    console.warn(
      "[fetchOrgLogo] threw:",
      err?.name,
      err?.message
    );
    return null;
  }
}

// Per-leg outcome reported to callers so the desktop app can gate its
// write-back: only hydrate (enable auto-save) when the legs that the save
// would clobber actually succeeded.
export type FetchLegStatus = {
  userLegOk: boolean;
  orgTextLegOk: boolean;
  logoLegOk: boolean;
};

export async function fetchSettings(
  orgId: string,
  userId: string,
  opts?: { onStatus?: (status: FetchLegStatus) => void }
): Promise<AppUserSettings> {
  if (!orgId || !userId) {
    opts?.onStatus?.({ userLegOk: false, orgTextLegOk: false, logoLegOk: false });
    return readCachedSettings() ?? { ...DEFAULT_SETTINGS };
  }

  const tStart =
    typeof performance !== "undefined" ? performance.now() : Date.now();

  // Three independent queries. The logo is the only one with a timeout —
  // text/user are small (<1 KB) and shouldn't ever hang, but if they do
  // they'll surface as their own rejected promise via allSettled.
  const orgTextP = supabase
    .from("org_settings")
    .select(ORG_TEXT_COLUMNS)
    .eq("org_id", orgId)
    .maybeSingle();
  const userP = supabase
    .from("user_settings")
    .select(USER_COLUMNS)
    .eq("user_id", userId)
    .maybeSingle();
  // supabase-js query builders are thenable but not real Promises —
  // wrap with Promise.resolve so withTimeout can race against them.
  const orgLogoP = withTimeout(
    Promise.resolve(
      supabase
        .from("org_settings")
        .select(ORG_LOGO_COLUMN)
        .eq("org_id", orgId)
        .maybeSingle()
    ),
    LOGO_FETCH_TIMEOUT_MS,
    "orgLogo"
  );

  const [orgTextRes, userRes, orgLogoRes] = await Promise.allSettled([
    orgTextP,
    userP,
    orgLogoP,
  ]);

  const elapsed = Math.round(
    (typeof performance !== "undefined" ? performance.now() : Date.now()) -
      tStart
  );

  // ---- Diagnostic logging (Step 1) -----------------------------------
  // Per-leg outcomes so Safari Web Inspector shows exactly which query
  // failed and how long the slow one took. Greppable as [fetchSettings].
  const summarize = (
    label: string,
    res: PromiseSettledResult<any>,
    extra?: Record<string, any>
  ) => {
    if (res.status === "fulfilled") {
      const v: any = res.value;
      const errCode = v?.error?.code;
      const ok = !v?.error || errCode === "PGRST116";
      dlog("[fetchSettings]", label, ok ? "ok" : "soft-err", {
        errCode,
        errMessage: v?.error?.message,
        hasData: !!v?.data,
        ...(extra || {}),
      });
    } else {
      const e: any = res.reason;
      // eslint-disable-next-line no-console
      console.warn("[fetchSettings]", label, "REJECTED:", {
        name: e?.name,
        message: e?.message,
      });
    }
  };
  summarize("orgText", orgTextRes);
  summarize("user", userRes);
  summarize("orgLogo", orgLogoRes, {
    logoLen:
      orgLogoRes.status === "fulfilled"
        ? ((orgLogoRes.value as any)?.data?.logo_data_url || "").length
        : 0,
  });
  dlog("[fetchSettings] total elapsed:", elapsed, "ms");
  // --------------------------------------------------------------------

  const okData = (res: PromiseSettledResult<any>): any => {
    if (res.status !== "fulfilled") return null;
    const v: any = res.value;
    if (v?.error && (v.error as any).code !== "PGRST116") return null;
    return v?.data ?? null;
  };
  const orgTextRow: any = okData(orgTextRes);
  const userRow: any = okData(userRes);
  const orgLogoRow: any = okData(orgLogoRes);

  // A leg "failed" when it rejected or returned a real error (NOT the benign
  // PGRST116 "no rows" — that's a legitimately empty/absent row). Failed legs
  // must NOT emit blanks that overwrite good data; they fall back to cache.
  const legFailed = (res: PromiseSettledResult<any>): boolean =>
    res.status === "rejected" ||
    (res.status === "fulfilled" &&
      !!(res.value as any)?.error &&
      ((res.value as any).error.code) !== "PGRST116");
  const orgTextLegFailed = legFailed(orgTextRes);
  const userLegFailed = legFailed(userRes);
  const logoLegFailed = legFailed(orgLogoRes);

  // Report per-leg outcome BEFORE any early return (incl. auto-migrate) so the
  // caller always learns whether the fetch was authoritative.
  opts?.onStatus?.({
    userLegOk: !userLegFailed,
    orgTextLegOk: !orgTextLegFailed,
    logoLegOk: !logoLegFailed,
  });

  // Auto-migrate: nothing in Supabase at all → push cache up. Same trigger
  // as before, but checks all three legs since the org row is now split.
  if (!orgTextRow && !userRow && !orgLogoRow && !logoLegFailed) {
    const cached = readCachedSettings();
    if (hasMeaningfulData(cached)) {
      try {
        await upsertSettings(orgId, userId, cached as AppUserSettings);
        return cached as AppUserSettings;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("Settings auto-migration failed; using cache:", err);
        return cached as AppUserSettings;
      }
    }
  }

  // Logo precedence. The intent is narrow: preserve a previously cached
  // logoDataUrl ONLY when this fetch's logo leg actually failed (timed out
  // or threw). If the logo leg succeeded — even with an empty/null value —
  // we trust the cloud (the user may have legitimately cleared their logo
  // on desktop). This avoids the cellular-timeout regression without
  // accidentally resurrecting a logo the user deleted.
  const cached = readCachedSettings();
  const cachedLogo = (cached?.logoDataUrl || "") as string;
  const cloudLogo = (orgLogoRow?.logo_data_url || "") as string;
  const effectiveLogo = logoLegFailed ? cachedLogo : cloudLogo;

  // Approach (1) — preserve-on-failure for the text legs, mirroring the logo
  // above. When a leg failed, read the field from the last-known-good cache
  // instead of emitting a blank. A leg that SUCCEEDED (even with an empty
  // value) still trusts the cloud, so genuinely-empty fields read as empty.
  const orgTextVal = (col: string, key: keyof AppUserSettings, def: any) =>
    orgTextLegFailed ? ((cached as any)?.[key] ?? def) : (orgTextRow?.[col] ?? def);
  const userVal = (col: string, key: keyof AppUserSettings, def: any) =>
    userLegFailed ? ((cached as any)?.[key] ?? def) : (userRow?.[col] ?? def);

  // Merge org + user + defaults
  const merged: AppUserSettings = {
    ...DEFAULT_SETTINGS,
    // org text (cache-preserving when the org-text leg failed)
    logoSlogan: orgTextVal("logo_slogan", "logoSlogan", ""),
    companyName: orgTextVal("company_name", "companyName", DEFAULT_SETTINGS.companyName),
    companyPhone: orgTextVal("company_phone", "companyPhone", ""),
    companyAddress: orgTextVal("company_address", "companyAddress", ""),
    companyWebsite: orgTextVal("company_website", "companyWebsite", ""),
    license: orgTextVal("license", "license", ""),
    emailSubjectTemplate: orgTextVal(
      "email_subject_template",
      "emailSubjectTemplate",
      DEFAULT_SETTINGS.emailSubjectTemplate
    ),
    emailBodyTemplate: orgTextVal(
      "email_body_template",
      "emailBodyTemplate",
      DEFAULT_SETTINGS.emailBodyTemplate
    ),
    proposalLayoutOrder: orgTextLegFailed
      ? (Array.isArray(cached?.proposalLayoutOrder) ? cached!.proposalLayoutOrder : [])
      : (Array.isArray(orgTextRow?.proposal_layout_order) ? orgTextRow.proposal_layout_order : []),
    proposalText: normalizeProposalText(
      orgTextVal("proposal_text", "proposalText", {})
    ),
    // logo (decoupled — preserves cached value if cloud leg failed)
    logoDataUrl: effectiveLogo,
    // user-level (cache-preserving when the user leg failed)
    userName: userVal("user_name", "userName", DEFAULT_SETTINGS.userName),
    userPhone: userVal("user_phone", "userPhone", ""),
    userEmail: userVal("user_email", "userEmail", ""),
    signatureUrl: userVal("signature_url", "signatureUrl", ""),
  };

  writeCachedSettings(merged);
  return merged;
}

// ----------------------------------------------------------------------------
// Upsert: split UI shape into org + user payloads, upsert both.
// ----------------------------------------------------------------------------
export async function upsertSettings(
  orgId: string,
  userId: string,
  settings: AppUserSettings,
  opts?: {
    allowClear?: boolean;
    /** Settings keys the user deliberately emptied (Settings reports them).
     *  The blank-guard below is skipped for exactly these keys, so an
     *  intentional clear saves as empty instead of silently reverting. */
    clearKeys?: string[];
  }
): Promise<void> {
  if (!orgId || !userId) throw new Error("Missing orgId or userId");

  // Approach (2) — write-side backstop. Unless the caller explicitly allows
  // clearing, never overwrite a currently non-empty column with a blank. We
  // read the current text rows as the "previous good" reference (the local
  // cache is unreliable here — App.tsx rewrites it on every keystroke before
  // this debounced save runs, so it can already hold the blank). The logo
  // blob is intentionally excluded from this read (large, and already
  // protected on the fetch/merge side). If the reference read fails (e.g.
  // offline) we abort rather than risk a destructive write — a flaky save
  // must never blank good data.
  let guarded: AppUserSettings = { ...settings };
  if (!opts?.allowClear) {
    const [orgCur, userCur] = await Promise.all([
      supabase.from("org_settings").select(ORG_TEXT_COLUMNS).eq("org_id", orgId).maybeSingle(),
      supabase.from("user_settings").select(USER_COLUMNS).eq("user_id", userId).maybeSingle(),
    ]);
    if (orgCur.error && (orgCur.error as any).code !== "PGRST116") throw orgCur.error;
    if (userCur.error && (userCur.error as any).code !== "PGRST116") throw userCur.error;

    // A plain object (proposal_text) counts as blank when it holds nothing.
    const isPlainObject = (v: any) =>
      !!v && typeof v === "object" && !Array.isArray(v);
    const isBlank = (v: any) =>
      v === null ||
      v === undefined ||
      (typeof v === "string" && v.trim() === "") ||
      (Array.isArray(v) && v.length === 0) ||
      (isPlainObject(v) && Object.keys(v).length === 0);
    const nonEmpty = (v: any) =>
      (typeof v === "string" && v.trim() !== "") ||
      (Array.isArray(v) && v.length > 0) ||
      (isPlainObject(v) && Object.keys(v).length > 0);

    const GUARD_FIELDS: Array<{ key: keyof AppUserSettings; col: string; src: "org" | "user" }> = [
      { key: "userName", col: "user_name", src: "user" },
      { key: "userPhone", col: "user_phone", src: "user" },
      { key: "userEmail", col: "user_email", src: "user" },
      { key: "signatureUrl", col: "signature_url", src: "user" },
      { key: "companyName", col: "company_name", src: "org" },
      { key: "companyPhone", col: "company_phone", src: "org" },
      { key: "companyAddress", col: "company_address", src: "org" },
      { key: "companyWebsite", col: "company_website", src: "org" },
      { key: "license", col: "license", src: "org" },
      { key: "logoSlogan", col: "logo_slogan", src: "org" },
      { key: "emailSubjectTemplate", col: "email_subject_template", src: "org" },
      { key: "emailBodyTemplate", col: "email_body_template", src: "org" },
      { key: "proposalLayoutOrder", col: "proposal_layout_order", src: "org" },
      { key: "proposalText", col: "proposal_text", src: "org" },
    ];
    const clearKeys = new Set(opts?.clearKeys || []);
    for (const f of GUARD_FIELDS) {
      if (clearKeys.has(String(f.key))) continue; // user emptied it on purpose
      const row: any = f.src === "org" ? orgCur.data : userCur.data;
      const prevVal = row?.[f.col];
      if (isBlank((guarded as any)[f.key]) && nonEmpty(prevVal)) {
        (guarded as any)[f.key] = prevVal;
      }
    }
  }

  const now = new Date().toISOString();

  const orgPayload = {
    org_id: orgId,
    logo_data_url: guarded.logoDataUrl || null,
    logo_slogan: guarded.logoSlogan || null,
    company_name: guarded.companyName || null,
    company_phone: guarded.companyPhone || null,
    company_address: guarded.companyAddress || null,
    company_website: guarded.companyWebsite || null,
    license: guarded.license || null,
    email_subject_template: guarded.emailSubjectTemplate || null,
    email_body_template: guarded.emailBodyTemplate || null,
    proposal_layout_order: Array.isArray(guarded.proposalLayoutOrder)
      ? guarded.proposalLayoutOrder
      : [],
    // Blank is never stored: an empty object saves as NULL (= built-in text).
    proposal_text: isProposalTextEmpty(guarded.proposalText)
      ? null
      : normalizeProposalText(guarded.proposalText),
    updated_at: now,
    updated_by: userId,
  };

  const userPayload = {
    user_id: userId,
    org_id: orgId,
    user_name: guarded.userName || null,
    user_phone: guarded.userPhone || null,
    user_email: guarded.userEmail || null,
    signature_url: guarded.signatureUrl || null,
    updated_at: now,
  };

  const [orgRes, userRes] = await Promise.all([
    supabase.from("org_settings").upsert(orgPayload, { onConflict: "org_id" }),
    supabase
      .from("user_settings")
      .upsert(userPayload, { onConflict: "user_id" }),
  ]);

  if (orgRes.error) throw orgRes.error;
  if (userRes.error) throw userRes.error;

  writeCachedSettings(guarded);
}
